import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config';
import { format, parseISO, isValid } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import VisitsSelectionHeader from '../components/Visits/VisitsSelectionHeader';
import { getVisitsTabs } from '../components/Visits/VisitsTabs';
import ViewerSidebar, { ACCENT_BLUE } from '../components/ui/ViewerSidebar';
import CardSelect from '../components/ui/CardSelect';
import { VisitsProvider, useVisits } from '../contexts/VisitsContext';
import useAnalytics from '../hooks/useAnalytics';
import useTabParam from '../hooks/useTabParam.js';
import usePrunedSelection from '../hooks/usePrunedSelection.js';
import useVisitsConfig from '../hooks/useVisitsConfig.js';
import {
  isCountableVisit,
  isCompletedVisit,
  VISIT_STATUS,
  statusLabel,
  statusColor,
} from '../constants/visitsClassification';

// Moved to utils so components can obfuscate without importing this page.
// Re-exported here: the existing Visits components import it from this module.
import { obfuscateUsername } from '../utils/obfuscateUsername';
import { DEFAULT_COMPARISON_MODE } from '../constants/visitsComparison';
import { DEFAULT_SOURCE, sourceParam } from '../constants/visitsSource';
import {
  UNIVERS_ALL,
  UNIVERS_ALL_LABEL,
  distinctUnivers,
  hasUniversSplit,
  matchesUnivers,
  visitsWithDuration,
  averageDurationByUnivers,
  minutes,
  countStoreVisits,
  universLabel,
} from '../constants/visitsUnivers';
export { obfuscateUsername } from '../utils/obfuscateUsername';

// Distinct, sorted select options from a list of raw values. The type guard is
// not decoration: localeCompare throws on a non-string, and this runs in a memo
// during render on a page with no boundary — one odd payload value would white
// out the whole viewer.
const optionsFrom = (list) => [...new Set(list.filter((v) => typeof v === 'string' && v))]
  .sort((a, b) => a.localeCompare(b, 'fr'))
  .map((name) => ({ value: name, label: name }));

function VisitsViewerContent({ fetchWithAuth, currentUser }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedProject, setSelectedProject] = useState(null);

  // Store initial obfuscate param in a ref to persist it
  const obfuscateRef = useRef(searchParams.has('obfuscate'));
  const obfuscate = obfuscateRef.current;

  const jailRef = useRef(searchParams.has('jail'));
  const jail = jailRef.current;

  const { track } = useAnalytics({ currentUser, project: selectedProject?.label, module: 'TH_VISITS' });

  // State pour les données et sélections
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);

  const [previousVisits, setPreviousVisits] = useState([]);

  // Multi-select: a regional manager filters on their whole team at once, not one
  // rep at a time. Matching stays on `username`, which is what the payload carries
  // (real name + sector for admins, initials otherwise) — the option values come
  // from the same field, so both sides agree.
  const [selectedSalespeople, setSelectedSalespeople] = useState([]);


  const [selectedShopSigns, setSelectedShopSigns] = useState([]);

  // Single choice, unlike the other filters: an univers is a lens on the same
  // visits, not a subset to combine. UNIVERS_ALL keeps every row, which is when the
  // per-univers breakdown is worth drawing.
  const [selectedUnivers, setSelectedUnivers] = useState(UNIVERS_ALL);

  // Business rule: this is a STORE-visit report. Deleted rows, placeholders (ADMIN,
  // BOX, EVENT, salons…) and visits the promoter declared did not happen are out of
  // scope and hidden everywhere by default — they polluted the Détails table and
  // skewed the Durées averages. Admins can bring them back to check a discrepancy.
  const [showOutOfScope, setShowOutOfScope] = useState(false);
  // "Voir en tant que client": a rendering preview, never an authorization
  // change. It hides the admin tabs and masks names so an admin can check what a
  // client actually sees — the requests and the data are untouched.
  const [viewAsUser, setViewAsUser] = useState(false);

  // What the variations compare against. Y-1 by default (same period, previous
  // year): comparing a short month or
  // a promotional week to the period just before it describes the calendar, not the
  // work. The backend echoes back the window it actually used.
  const [comparisonMode, setComparisonMode] = useState(DEFAULT_COMPARISON_MODE);

  const { startDate, endDate } = useVisits();

  // Handler pour les changements de projet
  const handleProjectChange = useCallback((newProject) => {
    // Mettre à jour le projet sélectionné
    setSelectedProject(newProject);

    // Créer un nouvel objet searchParams pour éviter les mutations
    const newSearchParams = new URLSearchParams(searchParams);

    if (newProject) {
      newSearchParams.set('projectId', newProject.value);
      newSearchParams.set('source', newProject.sourceSystem);
    } else {
      newSearchParams.delete('projectId');
      newSearchParams.delete('source');
    }

    // Conserver les autres paramètres (comme tab)
    setSearchParams(newSearchParams);

    // Réinitialiser la sélection du vendeur
    setSelectedSalespeople([]);
  }, [searchParams, setSearchParams]);

  // Récupération des projets - une seule fois au chargement initial
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(`${API_BASE_URL}/api/dwh/visits/projects`);

        if (response.ok) {
          const projectsData = await response.json();
          // One table now holds several source systems, and a campaign id is only
          // unique WITHIN a source, so a project is the PAIR (source, projectId).
          // `label` already reads "SOURCE — PROJET", built server-side.
          const formattedProjects = projectsData.map(project => ({
            value: project.projectId,
            label: project.label,
            sourceSystem: project.sourceSystem,
            projectName: project.projectName,
            supportsQuality: project.supportsQuality,
          }));
          setProjects(formattedProjects);

          // Auto-select initial project from URL if present. A link written before
          // the multi-source rollout carries only ?projectId=, and those were all
          // TeamHaven — hence the default.
          const projectId = searchParams.get('projectId');
          const source = searchParams.get('source') || DEFAULT_SOURCE;

          if (projectId && !selectedProject) {
            const projectFromUrl = formattedProjects.find(
              p => p.value.toString() === projectId && p.sourceSystem === source
            );

            if (projectFromUrl) {
              setSelectedProject(projectFromUrl);
            }
          }
        } else {
          console.error("Failed to fetch projects");
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Effet pour gérer les changements de projectId dans l'URL après le montage initial
  useEffect(() => {
    const projectId = searchParams.get('projectId');
    const source = searchParams.get('source') || DEFAULT_SOURCE;
    const currentProjectId = selectedProject?.value?.toString();
    const currentSource = selectedProject?.sourceSystem;

    // Ne mettre à jour que si:
    // 1. Il y a un projectId dans l'URL
    // 2. La paire (source, projet) est différente de la sélection actuelle
    // 3. Les projets sont déjà chargés
    const pairChanged = projectId !== currentProjectId || source !== currentSource;
    if (projectId && pairChanged && projects.length > 0) {
      const projectFromUrl = projects.find(
        p => p.value.toString() === projectId && p.sourceSystem === source
      );
      if (projectFromUrl) {
        setSelectedProject(projectFromUrl);
      }
    }
  }, [searchParams, projects, selectedProject]);

  // Récupération des visites quand le projet change
  useEffect(() => {
    const fetchVisits = async () => {
      if (!selectedProject) return;

      try {
        setLoading(true);
        const formatDate = (date) => {
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${year}-${month}-${day}`;
        };

        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);

        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/visits/${selectedProject.value}`
          + `?${sourceParam(selectedProject)}`
          + `&startDate=${formattedStartDate}&endDate=${formattedEndDate}&comparison=${comparisonMode}`
        );

        if (response.ok) {
          const data = await response.json()
          const visits = data.visits;
          const previousVisits = data.previousVisits;

          const formatVisit = (visit) => {
            // Gérer correctement le parsing des dates avec timezone
            let dateObj = null;
            let plannedStartObj = null;

            // Parsing de la date de visite réelle si présente
            if (visit.date) {
              // Supprimer le timezone et parser uniquement la partie date et heure
              const datePart = visit.date.split(' +')[0];
              dateObj = parseISO(datePart);

              // Vérifier si le parsing a réussi
              if (!isValid(dateObj)) {
                console.warn(`Parsing invalide pour la date: ${visit.date}`);
                dateObj = null;
              }
            }

            // Parsing de la date planifiée
            if (visit.plannedStart) {
              // Supprimer le timezone et parser uniquement la partie date et heure
              const plannedDatePart = visit.plannedStart.split(' +')[0];
              plannedStartObj = parseISO(plannedDatePart);

              // Vérifier si le parsing a réussi
              if (!isValid(plannedStartObj)) {
                console.warn(`Parsing invalide pour la date planifiée: ${visit.plannedStart}`);
                plannedStartObj = null;
              }
            }

            return {
              ...visit,
              date: dateObj,
              plannedStart: plannedStartObj,
              // Labels and colors come from the shared status vocabulary — see
              // src/constants/visitsClassification.js. They used to be restated in
              // this file with a second palette that disagreed with the calendar
              // and the lexicon.
              statusLabel: statusLabel(visit.status),
              statusColor: statusColor(visit.status),
              // One rule for the three duration columns: a missing value stays
              // null, a measured 0 stays 0. `parseInt` turned 20.9 into 20 and
              // `x ? … : null` turned a real 0 into null.
              actualDurationMinutes: minutes(visit.actualDuration),
              // This row's share of the physical visit's duration — see
              // visitsWithDuration() for which of the two each lens reads.
              allocatedDurationMinutes: minutes(visit.allocatedDuration),
              plannedDurationMinutes: minutes(visit.plannedDuration)
            };
          };

          // Formater les données des visites
          const formattedVisits = visits.map(visit => formatVisit(visit));
          const formattedPreviousVisits = previousVisits.map(visit => formatVisit(visit));

          setVisits(formattedVisits);
          setPreviousVisits(formattedPreviousVisits);

          // Réinitialiser les enseignes sélectionnées quand le projet change
          setSelectedShopSigns([]);
          setSelectedUnivers(UNIVERS_ALL);
        } else {
          console.error("Failed to fetch visits");
        }
      } catch (error) {
        console.error("Error fetching visits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVisits();
  }, [selectedProject, startDate, endDate, comparisonMode, fetchWithAuth]);

  // Modifier le filtrage des visites pour prendre en compte à la fois le vendeur et les enseignes
  const filteredVisits = useMemo(() => {
    let filtered = visits;

    // Filtrer par vendeur si sélectionné
    if (selectedSalespeople && selectedSalespeople.length > 0) {
      const usernames = selectedSalespeople.map(p => p.value);
      filtered = filtered.filter(visit => usernames.includes(visit.username));
    }

    // Filtrer par enseignes si sélectionnées
    if (selectedShopSigns && selectedShopSigns.length > 0) {
      const shopSignValues = selectedShopSigns.map(sign => sign.value);
      filtered = filtered.filter(visit => visit.shopSign && shopSignValues.includes(visit.shopSign));
    }

    // Filtrer par univers. Choix unique : sur UNIVERS_ALL tout est conservé, donc
    // une visite couvrant deux univers compte deux fois — ce qui est la règle
    // métier. Sur un univers précis chaque visite ne contribue qu'une ligne, et
    // « visites » et « visites magasin » convergent.
    if (selectedUnivers && selectedUnivers !== UNIVERS_ALL) {
      filtered = filtered.filter(visit => matchesUnivers(visit, selectedUnivers));
    }

    return filtered;
  }, [visits, selectedSalespeople, selectedShopSigns, selectedUnivers]);

  // What the tabs are built against while the client view is on. Only the
  // RENDERING changes: the payload, the filters and the requests are the same.
  const viewedAsUser = viewAsUser
    ? { ...currentUser, isadmin: false, issuperadmin: false }
    : currentUser;
  const viewedObfuscate = obfuscate || viewAsUser;
  // Out-of-scope rows are an admin signal; a client never sees them.
  const viewedOutOfScope = showOutOfScope && !viewAsUser;

  // The visits this user may see, before their own filter choices. The option
  // lists derive from here, not from the raw payload: an ADMIN placeholder used
  // to put its shop sign in a client's "Enseignes" list, where picking it
  // emptied the page.
  const permittedVisits = useMemo(
    () => (viewedOutOfScope ? visits : visits.filter(isCountableVisit)),
    [visits, viewedOutOfScope]
  );

  const salespeople = useMemo(
    () => optionsFrom(permittedVisits.map((v) => v.username)),
    [permittedVisits]
  );
  const shopSigns = useMemo(
    () => optionsFrom(permittedVisits.map((v) => v.shopSign)),
    [permittedVisits]
  );

  const universOptions = useMemo(() => {
    // `hasUniversSplit` on the full payload: whether a project splits by univers
    // is a property of the project, not of a row-visibility checkbox. The
    // OPTIONS still come from what the viewer may see.
    if (!hasUniversSplit(visits)) return [];
    return [
      { value: UNIVERS_ALL, label: UNIVERS_ALL_LABEL },
      ...distinctUnivers(permittedVisits).map((u) => ({ value: u, label: universLabel(u) })),
    ];
  }, [visits, permittedVisits]);

  const universSplit = universOptions.length > 0;

  // Three lists that can shrink at runtime, three selections that must not
  // outlive their option — see usePrunedSelection.
  usePrunedSelection(shopSigns, selectedShopSigns, setSelectedShopSigns);
  usePrunedSelection(salespeople, selectedSalespeople, setSelectedSalespeople);
  usePrunedSelection(universOptions, selectedUnivers, setSelectedUnivers, UNIVERS_ALL);

  // What the tabs actually display. `stats` deliberately keeps working off
  // `filteredVisits`: the headline tiles already exclude out-of-scope rows, and the
  // admin counters (supprimées, non effectuées, spéciales par type) need them present
  // to count them at all.
  const visibleVisits = useMemo(
    () => (viewedOutOfScope ? filteredVisits : filteredVisits.filter(isCountableVisit)),
    [filteredVisits, viewedOutOfScope]
  );

  // What the checkbox is hiding, broken down. A single total told the admin nothing
  // about whether the gap comes from deleted rows or from placeholders.
  const outOfScopeBreakdown = useMemo(() => {
    const out = filteredVisits.filter((v) => !isCountableVisit(v));
    return {
      total: out.length,
      deleted: out.filter((v) => v.isDeleted).length,
      special: out.filter((v) => !v.isDeleted && v.specialType).length,
      didNotHappen: out.filter((v) => !v.isDeleted && !v.specialType && v.didNotHappen).length,
    };
  }, [filteredVisits]);

  // Statistiques calculées
  const stats = useMemo(() => {
    // Fonction pour calculer les statistiques d'une période
    const calculatePeriodStats = (visitsData, isOngoingPeriod = false) => {
      // Only real, non-deleted, actually-held store visits count toward analytics.
      // Deleted / special / did-not-happen rows stay in visitsData for the admin widgets.
      const countable = visitsData.filter(isCountableVisit);
      const completedVisits = visitsData.filter(isCompletedVisit);

      // Nombre total de visites
      const totalPlannedVisits = countable.filter(({ plannedStart }) => !!plannedStart).length;
      const totalCompletedVisits = completedVisits.length;
      const totalMissedVisits = countable.filter(({ status }) => status === VISIT_STATUS.MISSED).length;

      // Signaux admin (calculés sur les lignes brutes, hors périmètre des stats)
      const totalDeletedVisits = visitsData.filter((v) => v.isDeleted).length;
      const totalDidNotHappen = visitsData.filter((v) => v.didNotHappen).length;
      const specialVisitsByType = visitsData.reduce((acc, v) => {
        if (v.specialType) acc[v.specialType] = (acc[v.specialType] || 0) + 1;
        return acc;
      }, {});
      const totalSpecialVisits = Object.values(specialVisitsByType).reduce((a, b) => a + b, 0);

      // Calcul du nombre moyen de visites par jour
      const uniqueDays = new Set();

      // Filtrer les visites complétées avec date valide
      completedVisits.filter(visit => visit.date).forEach(visit => {
        // Format pour le jour: YYYY-MM-DD
        const dayKey = format(visit.date, 'yyyy-MM-dd');
        uniqueDays.add(dayKey);
      });

      // Nombre de jours avec au moins une visite
      const activeDays = uniqueDays.size;

      // Calculer la moyenne arrondie à 1 décimale
      const visitsPerDay = activeDays > 0
        ? parseFloat((totalCompletedVisits / activeDays).toFixed(1))
        : 0;

      // Calcul du nombre moyen de visites par jour par utilisateur
      const uniqueUsers = new Set(completedVisits.map(v => v.username)).size;
      const visitsPerDayPerUser = (uniqueUsers > 0 && activeDays > 0)
        ? parseFloat((totalCompletedVisits / uniqueUsers / activeDays).toFixed(1))
        : 0;

      // Taux de réalisation

      let completionRate = 0;
      if (isOngoingPeriod) {
        const today = new Date();
        const startOf = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const endOf = new Date(startOf.getTime() - 1);

        const completedVisitsToDate = completedVisits.filter(visit =>
          visit.date && visit.date <= endOf
        ).length;

        completionRate = totalPlannedVisits > 0
          ? Math.round((completedVisitsToDate / totalPlannedVisits) * 100)
          : 0;
      } else {
        completionRate = totalPlannedVisits > 0
          ? Math.round((totalCompletedVisits / totalPlannedVisits) * 100)
          : 0;
      }


      // Durée moyenne = the average of the univers-allocated durations, on every
      // lens. See visitsWithDuration() for why the physical reading was dropped.
      const durationRows = visitsWithDuration(completedVisits, selectedUnivers);
      const averageDuration = durationRows.length > 0
        ? Math.round(durationRows.reduce((acc, v) => acc + v.durationMinutes, 0) / durationRows.length)
        : 0;
      // What the average is made of. Without it the tile is a single number a
      // reader cannot check against the per-enseigne chart beside it.
      const durationByUnivers = averageDurationByUnivers(completedVisits);

      // Nombre de magasins différents visités
      const uniqueStores = new Set(completedVisits.filter(v => v.date).map(v => v.interventionPoint)).size;

      // Graphique de durée des visites par jour de la semaine
      const durationByDay = Array(7).fill(0).map(() => ({ count: 0, duration: 0 }));

      // Same population as the tile, so the two can never tell different stories.
      // No duration guard: visitsWithDuration already returns finite minutes only,
      // zeros included.
      durationRows.forEach(visit => {
        if (visit.date) {
          const dayOfWeek = visit.date.getDay();
          if (durationByDay[dayOfWeek]) {
            durationByDay[dayOfWeek].count++;
            durationByDay[dayOfWeek].duration += visit.durationMinutes;
          }
        }
      });

      const avgDurationByDay = durationByDay.map(day =>
        day && day.count > 0 ? Math.round(day.duration / day.count) : 0
      );

      const salesMap = new Map();

      completedVisits.forEach(visit => {
        if (!visit.username) return;

        if (!salesMap.has(visit.username)) {
          salesMap.set(visit.username, {
            username: visit.username,
            totalVisits: 0,
            completedVisits: 0,
            uniqueDays: new Set(),
            uniqueWeeks: new Set(),
            totalDuration: 0
          });
        }

        const salesData = salesMap.get(visit.username);
        salesData.totalVisits++;
        salesData.completedVisits++;

        const dayKey = format(visit.date, 'yyyy-MM-dd');
        salesData.uniqueDays.add(dayKey);
      });

      const salespeopleSummary = Array.from(salesMap.values()).map(salesData => ({
        username: salesData.username,
        totalVisits: salesData.totalVisits,
        completedVisits: salesData.completedVisits,
        uniqueDays: salesData.uniqueDays.size,
        avgVisitsPerDay: (salesData.totalVisits / salesData.uniqueDays.size).toFixed(1),
      }));

      // Répartition des statuts. "Réalisée/Planifiée/Manquée/Inconnue" portent sur les
      // visites comptabilisées ; "Non effectuée" (gate=false) et "Supprimée" sont des
      // signaux admin issus des lignes brutes, hors périmètre des stats.
      const statusCounts = {
        "Planifiée": countable.filter(v => v.status === VISIT_STATUS.PLANNED).length,
        "Réalisée": totalCompletedVisits,
        "Manquée": countable.filter(v => v.status === VISIT_STATUS.MISSED).length,
        "Non effectuée": totalDidNotHappen,
        "Supprimée": totalDeletedVisits,
        "Inconnue": countable.filter(v => v.status === VISIT_STATUS.UNKNOWN).length
      };

      // Calcul du nombre total de jours de travail (somme des jours distincts par vendeur)
      const userWorkDays = {};

      completedVisits.forEach(visit => {
        if (!visit.username || !visit.date) return;

        if (!userWorkDays[visit.username]) {
          userWorkDays[visit.username] = new Set();
        }

        // Ajouter ce jour pour cet utilisateur
        const dayKey = format(visit.date, 'yyyy-MM-dd');
        userWorkDays[visit.username].add(dayKey);
      });

      // Sommer le nombre de jours distincts pour chaque utilisateur
      const totalWorkDays = Object.values(userWorkDays).reduce(
        (total, days) => total + days.size,
        0
      );

      return {
        // Univers-visits (rows) vs PHYSICAL store visits (distinct visit ids). Every
        // other figure here is in univers-visits, which is the business rule for a
        // client watching several univers; this pair is what makes the doubling
        // legible instead of surprising.
        totalStoreVisits: countStoreVisits(countable),
        totalCompletedStoreVisits: countStoreVisits(completedVisits),
        totalPlannedVisits,
        totalCompletedVisits,
        totalDeletedVisits,
        totalDidNotHappen,
        totalSpecialVisits,
        specialVisitsByType,
        totalMissedVisits,
        visitsPerDay,
        visitsPerDayPerUser,
        activeDays,
        uniqueUsers,
        completionRate,
        averageDuration,
        durationByUnivers,
        uniqueStores,
        avgDurationByDay,
        statusCounts,
        salespeopleSummary,
        avgVisitsPerDayPerUser: salespeopleSummary.length > 0
          ? parseFloat((salespeopleSummary.reduce((acc, curr) => acc + parseFloat(curr.avgVisitsPerDay), 0) / salespeopleSummary.length).toFixed(1))
          : 0,
        avgVisitsPerDay: salespeopleSummary.length > 0 && salespeopleSummary.reduce((acc, curr) => acc + curr.uniqueDays, 0) > 0
          ? parseFloat((salespeopleSummary.reduce((acc, curr) => acc + curr.completedVisits, 0) / salespeopleSummary.reduce((acc, curr) => acc + curr.uniqueDays, 0)).toFixed(1))
          : 0,
        totalWorkDays,
        shouldShowToDate
      };
    };


    const hasSalespersonFilter = selectedSalespeople.length > 0;
    const hasShopSignsFilter = selectedShopSigns && selectedShopSigns.length > 0;
    // The univers lens belongs here too. Left out, "Visites effectuées" counted
    // every univers while "Durée moyenne" — which narrows internally — counted
    // one: two figures on the same card telling different stories.
    const hasUniversFilter = !!selectedUnivers && selectedUnivers !== UNIVERS_ALL;
    const hasAnyFilter = hasSalespersonFilter || hasShopSignsFilter || hasUniversFilter;
    const currentVisitsToAnalyze = hasAnyFilter ? filteredVisits : visits;

    const isCustomRange = startDate.getDate() !== 1 ||
      endDate.getDate() !== new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();

    const shouldShowToDate = isCustomRange;
    const currentStats = calculatePeriodStats(currentVisitsToAnalyze, shouldShowToDate);

    let previousVisitsToAnalyze = previousVisits;
    if (selectedSalespeople.length > 0) {
      const usernames = selectedSalespeople.map(p => p.value);
      previousVisitsToAnalyze = previousVisitsToAnalyze.filter(visit => usernames.includes(visit.username));
    }
    if (selectedShopSigns && selectedShopSigns.length > 0) {
      const shopSignValues = selectedShopSigns.map(sign => sign.value);
      previousVisitsToAnalyze = previousVisitsToAnalyze.filter(visit =>
        visit.shopSign && shopSignValues.includes(visit.shopSign)
      );
    }
    // Same lens on both sides, or Y-1 compares one univers against all of them.
    if (hasUniversFilter) {
      previousVisitsToAnalyze = previousVisitsToAnalyze.filter(
        (visit) => matchesUnivers(visit, selectedUnivers)
      );
    }
    const previousStats = calculatePeriodStats(previousVisitsToAnalyze);

    // Combiner les deux périodes pour la comparaison
    return {
      ...currentStats,
      previous: {
        ...previousStats
      },
      // Comparaisons (pour faciliter l'utilisation dans les StatTiles)
      comparisons: {
        totalCompletedVisits: {
          current: currentStats.totalCompletedVisits,
          previous: previousStats.totalCompletedVisits
        },
          totalMissedVisits: {
            current: currentStats.totalMissedVisits,
            previous: previousStats.totalMissedVisits
          },
        visitsPerDay: {
          current: currentStats.visitsPerDay,
          previous: previousStats.visitsPerDay
        },
        visitsPerDayPerUser: {
          current: currentStats.visitsPerDayPerUser,
          previous: previousStats.visitsPerDayPerUser
        },
        completionRate: {
          current: currentStats.completionRate,
          previous: previousStats.completionRate
        },
        averageDuration: {
          current: currentStats.averageDuration,
          previous: previousStats.averageDuration
        },
        uniqueStores: {
          current: currentStats.uniqueStores,
          previous: previousStats.uniqueStores
        },
        totalWorkDays: {
          current: currentStats.totalWorkDays,
          previous: previousStats.totalWorkDays
        }
      }
    };
  }, [filteredVisits, visits, previousVisits, selectedSalespeople, selectedShopSigns,
    selectedUnivers, startDate, endDate]);

  // Derived from `visits`, never from `filteredVisits`: options built off the
  // filtered set would vanish as soon as one is chosen.

  const { config: projectConfig, loaded: projectConfigLoaded } = useVisitsConfig({ selectedProject, fetchWithAuth });

  // Tabs (metadata + content) for the sidebar nav and the content area.
  const tabs = getVisitsTabs({
    universSplit,
    selectedUnivers,
    selectedProject,
    selectedShopSigns,
    visits: visibleVisits,
    stats,
    comparisonMode,
    showOutOfScope: viewedOutOfScope,
    startDate,
    endDate,
    selectedSalespeople,
    currentUser: viewedAsUser,
    fetchWithAuth,
    obfuscate: viewedObfuscate,
    projectConfig,
    projectConfigLoaded,
  });
  const tabIds = useMemo(() => tabs.map(t => t.id), [tabs]);
  const [activeTab, setActiveTab] = useTabParam(tabIds, 'summary');

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    track('visits_view_tab', { view_tab: tabId, project: selectedProject?.label });
  }, [setActiveTab, track, selectedProject]);

  const buildHref = useCallback((tabId) => {
    const next = new URLSearchParams(searchParams);
    if (tabId === 'summary') next.delete('tab'); else next.set('tab', tabId);
    const qs = next.toString();
    return qs ? `?${qs}` : '';
  }, [searchParams]);

  // Project selector lives in the sidebar. When obfuscated, options are masked for
  // display but we always store the real project (matched back by value).
  const projectOptions = obfuscate
    ? projects.map(p => ({ ...p, label: obfuscateUsername(p.label) }))
    : projects;
  const projectValue = selectedProject
    ? (obfuscate ? { ...selectedProject, label: obfuscateUsername(selectedProject.label) } : selectedProject)
    : null;
  const onProjectPick = (opt) => handleProjectChange(opt ? (projects.find(p => p.value === opt.value) || opt) : null);

  const activeContent = tabs.find(t => t.id === activeTab)?.content || tabs[0]?.content || null;

  // Stated, not implied: without it an admin wonders why the Contrôle qualité tab
  // vanished. One line on a laptop — the wrapped version was noisier than the
  // state it describes.
  const clientViewBanner = viewAsUser ? (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-purple-300 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 px-4 py-2">
      <p className="text-sm text-purple-900 dark:text-purple-200 truncate">
        <strong>Vue client</strong> — onglets admin, noms et hors périmètre masqués.
      </p>
      <button
        type="button"
        onClick={() => setViewAsUser(false)}
        aria-label="Quitter la vue client"
        className="shrink-0 rounded-md bg-purple-700 px-3 py-1 text-xs font-medium text-white hover:bg-purple-800"
      >
        Quitter
      </button>
    </div>
  ) : null;

  return (
    <div className="flex h-full overflow-hidden bg-gray-50 dark:bg-[#0d0d0d]">
      <ViewerSidebar
        header={!jail && (
          <CardSelect
            label="Projet"
            value={projectValue}
            onChange={onProjectPick}
            options={projectOptions}
            placeholder="Choisir un projet..."
            searchPlaceholder="Rechercher un projet..."
            sectionLabel="Vos projets"
          />
        )}
        showNav={!!selectedProject}
        collapsible
        storageKey="visits.sidebar.collapsed"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        buildHref={buildHref}
        navLabel="Visites Navigation"
        accent={ACCENT_BLUE}
      />

      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-[#0d0d0d]">
        <div className="container mx-auto p-4 text-left">
          {!selectedProject ? (
            <div className="flex flex-col items-center justify-center text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-10 mt-2">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-3H5a2 2 0 0 0-2 2z" />
                </svg>
              </span>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Aucun projet sélectionné</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                Sélectionnez un projet dans le menu à gauche pour visualiser les données de visites.
              </p>
            </div>
          ) : activeTab === 'lexicon' ? (
            // No filter bar here — the lexicon does not read them — but the
            // banner still belongs: the tab hides its admin definitions in
            // client view, and an unexplained shorter page reads as a bug.
            <div>
              {clientViewBanner}
              {activeContent}
            </div>
          ) : (
            <div key={selectedProject.value}>
              {/* The filter bar pins itself; everything under it scrolls, banner
                  included — the header keeps showing the mode is on. */}
              <VisitsSelectionHeader
                universOptions={universOptions}
                selectedUnivers={selectedUnivers}
                setSelectedUnivers={setSelectedUnivers}
                selectedProject={selectedProject}
                loading={loading}
                salespeople={salespeople}
                selectedSalespeople={selectedSalespeople}
                setSelectedSalespeople={setSelectedSalespeople}
                shopSigns={shopSigns}
                selectedShopSigns={selectedShopSigns}
                setSelectedShopSigns={setSelectedShopSigns}
                obfuscate={viewedObfuscate}
                currentUser={currentUser}
                showOutOfScope={showOutOfScope}
                setShowOutOfScope={setShowOutOfScope}
                comparisonMode={comparisonMode}
                setComparisonMode={setComparisonMode}
                outOfScope={outOfScopeBreakdown}
                viewAsUser={viewAsUser}
                setViewAsUser={setViewAsUser}
              />

              <div className="mt-4">
                {clientViewBanner}
                {loading ? (
                  <div className="flex justify-center my-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  activeContent
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VisitsViewer(props) {
  return (
    <VisitsProvider>
      <VisitsViewerContent {...props} />
    </VisitsProvider>
  );
}
export default VisitsViewer;